import { 
  Users, 
  CheckCircle2, 
  Target, 
  MessageSquare, 
  AlertTriangle,
  ArrowRight
} from "lucide-react";

const managerRights = [
  {
    right: "Know everything their team is working on",
    implication: "No tasks should reach their team without their knowledge"
  },
  {
    right: "Be the primary point of contact for their team",
    implication: "Executives should go through them, not around them"
  },
  {
    right: "Prioritize their team's work",
    implication: "Even urgent executive requests should be filtered through them"
  },
  {
    right: "Deliver feedback to their direct reports",
    implication: "Skip-level feedback undermines their authority"
  },
  {
    right: "Represent their team in meetings",
    implication: "Don't invite ICs to executive meetings and bypass the manager"
  },
  {
    right: "Make decisions within their domain",
    implication: "Don't override or second-guess without discussion"
  }
];

export const DepartmentalAutonomyAccountability = () => {
  return (
    <section className="py-10 sm:py-12">
      <h2 id="manager-authority" className="text-2xl sm:text-3xl font-bold text-foreground mb-4 flex items-center gap-2">
        <Users className="h-6 w-6 text-primary" />
        What Managers Deserve From Leadership
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        If you've put someone in a management role, you owe them real authority. Here's what that means:
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        {managerRights.map((item) => (
          <div 
            key={item.right}
            className="bg-card border border-border rounded-lg p-4 flex items-start gap-3"
          >
            <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground">{item.right}</p>
              <p className="text-sm text-muted-foreground mt-1">{item.implication}</p>
            </div>
          </div>
        ))}
      </div>

      {/* When Skip-Level IS Appropriate */}
      <div className="bg-card border border-border rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-primary" />
          When Skip-Level Communication IS Appropriate
        </h3>
        <p className="text-muted-foreground mb-4">
          There are a few legitimate exceptions to the "always go through the manager" rule:
        </p>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span><strong>Skip-level 1:1s</strong> — Scheduled, transparent meetings where executives hear from ICs (managers should know these happen)</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span><strong>Ethics/HR issues</strong> — Harassment, fraud, or safety concerns should have a direct escalation path</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span><strong>Manager is the problem</strong> — If the concern is about the manager, there must be an alternative channel</span>
          </li>
          <li className="flex items-start gap-2">
            <ArrowRight className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span><strong>True emergencies</strong> — Building on fire, security breach, etc. (but still loop in manager ASAP)</span>
          </li>
        </ul>
      </div>

      {/* Self-Check */}
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Leadership Self-Check
        </h3>
        <p className="text-muted-foreground mb-4">
          Ask yourself these questions to see if you're respecting the chain:
        </p>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span>When was the last time I assigned a task directly to someone who doesn't report to me?</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span>Do my managers learn about my conversations with their team from me—or from their team?</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span>When I have feedback about an IC, do I tell the manager first?</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <span>If I need something from a team, who do I Slack—the manager or individual ICs?</span>
          </li>
        </ul>
      </div>
    </section>
  );
};
